import { Button } from '@agentic-flow-studio/shared/ui/button';
import {
  ChevronDown,
  ChevronUp,
  FileSpreadsheet,
  FileText,
  Loader2,
  SlidersHorizontal,
  Sparkles,
  Tag,
  Upload,
  X,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

export type VerticalKey =
  | 'general'
  | 'trades'
  | 'logistics'
  | 'it-telecom'
  | 'healthcare'
  | 'facilities';

export const VERTICAL_OPTIONS: Array<{ key: VerticalKey; label: string; icon: string }> = [
  { key: 'trades', label: 'Trades & Maintenance (HVAC, Electrical, Plumbing)', icon: '🔧' },
  { key: 'logistics', label: 'Logistics & Couriers', icon: '🚚' },
  { key: 'it-telecom', label: 'IT & Telecommunications', icon: '💻' },
  { key: 'healthcare', label: 'Healthcare & Mobile Care', icon: '🩺' },
  { key: 'facilities', label: 'Facilities & Cleaning', icon: '🧹' },
  { key: 'general', label: 'General Field Service', icon: '📋' },
];

interface IngestUploadPanelProps {
  orgId?: string;
  workspaceId?: string;
  verticalKey: VerticalKey;
  onVerticalKeyChange: (key: VerticalKey) => void;
  customGuidelines: string;
  onCustomGuidelinesChange: (value: string) => void;
  pasteText: string;
  onPasteTextChange: (value: string) => void;
  selectedImageBase64: string | null;
  selectedImageMime: string;
  selectedFileName: string | null;
  onImageSelected: (base64: string, mime: string, fileName: string) => void;
  onDocumentSelected: (fileName: string, text: string) => void;
  onClearImage: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isExtracting: boolean;
}

export function IngestUploadPanel({
  orgId,
  workspaceId,
  verticalKey,
  onVerticalKeyChange,
  customGuidelines,
  onCustomGuidelinesChange,
  pasteText,
  onPasteTextChange,
  selectedImageBase64,
  selectedImageMime,
  selectedFileName,
  onImageSelected,
  onDocumentSelected,
  onClearImage,
  onSubmit,
  isExtracting,
}: IngestUploadPanelProps) {
  const [showAdvancedRules, setShowAdvancedRules] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Global clipboard paste listener for screenshots (Ctrl+V anywhere on the page)
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'TEXTAREA' ||
        (target.tagName === 'INPUT' && target.getAttribute('type') === 'text')
      ) {
        return;
      }

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf('image') !== -1) {
          const blob = item.getAsFile();
          if (blob) {
            e.preventDefault();
            const reader = new FileReader();
            reader.onload = (event) => {
              const result = event.target?.result as string;
              onImageSelected(result, blob.type || 'image/png', 'Pasted Screenshot');
            };
            reader.readAsDataURL(blob);
            break;
          }
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [onImageSelected]);

  const handleImageFile = (file: File) => {
    const isImage = file.type.startsWith('image/');
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (!isImage && !isPdf) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target?.result as string;
      onImageSelected(result, file.type || (isPdf ? 'application/pdf' : 'image/png'), file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleDocumentFile = async (file: File) => {
    const text = await file.text();
    onDocumentSelected(file.name, text);
  };

  const handleDragDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (
      file.type.startsWith('image/') ||
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf')
    ) {
      handleImageFile(file);
    } else {
      handleDocumentFile(file);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-4 rounded-xl border border-border/70 bg-muted/20">
      {/* Domain Vertical Selector Pills */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5 text-primary" />
              <span>Vertical Domain Knowledge</span>
            </span>
            {orgId && workspaceId && (
              <Link
                to={`/org/${orgId}/workspace/${workspaceId}/settings/job-plan`}
                className="text-[10px] text-muted-foreground hover:text-primary transition-colors underline"
              >
                (Set default in Settings)
              </Link>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowAdvancedRules(!showAdvancedRules)}
            className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
          >
            <SlidersHorizontal className="h-3 w-3" />
            <span>{showAdvancedRules ? 'Hide Custom Rules' : 'Custom Rules & Guidelines'}</span>
            {showAdvancedRules ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {VERTICAL_OPTIONS.map((opt) => {
            const isSelected = verticalKey === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => onVerticalKeyChange(opt.key)}
                className={`text-xs px-2.5 py-1 rounded-lg border transition-all inline-flex items-center gap-1.5 ${
                  isSelected
                    ? 'border-primary bg-primary text-primary-foreground font-medium shadow-xs'
                    : 'border-border/60 bg-background hover:bg-muted/60 text-muted-foreground'
                }`}
              >
                <span>{opt.icon}</span>
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>

        {showAdvancedRules && (
          <div className="mt-2 p-3 rounded-lg border border-border bg-background space-y-2 animate-in fade-in-50 duration-200">
            <label className="text-[11px] font-medium text-foreground">
              Workspace Custom Ingest Rules & Prompt Instructions
            </label>
            <textarea
              value={customGuidelines}
              onChange={(e) => onCustomGuidelinesChange(e.target.value)}
              placeholder="e.g. 'Job IDs always start with WO-. Priority 2 if water or electricity is mentioned. Default installation duration is 90 mins.'"
              rows={2}
              className="w-full p-2 text-xs rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary resize-none font-mono"
            />
            <span className="text-[10px] text-muted-foreground block">
              These rules are dynamically injected into the AI system prompt to customize extraction
              logic for your workspace.
            </span>
          </div>
        )}
      </div>

      {/* Multimodal Input Area / Drag & Drop */}
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDragDrop}
          className={`relative rounded-xl border-2 border-dashed transition-all p-4 flex flex-col items-center justify-center gap-2 text-center ${
            isDragOver
              ? 'border-primary bg-primary/5'
              : selectedImageBase64
                ? 'border-emerald-500/50 bg-emerald-500/[0.03]'
                : 'border-border/80 bg-background hover:border-primary/40'
          }`}
        >
          {selectedImageBase64 ? (
            <div className="flex items-center gap-4 w-full justify-between">
              <div className="flex items-center gap-3">
                {selectedImageMime === 'application/pdf' ||
                selectedFileName?.toLowerCase().endsWith('.pdf') ? (
                  <div className="h-16 w-20 rounded-lg bg-rose-500/10 border border-rose-500/20 flex items-center justify-center shrink-0">
                    <FileText className="h-8 w-8 text-rose-600 dark:text-rose-400" />
                  </div>
                ) : (
                  <img
                    src={selectedImageBase64}
                    alt="Screenshot Preview"
                    className="h-16 w-24 object-cover rounded-lg border border-border shadow-xs"
                  />
                )}
                <div className="text-left">
                  <span className="text-xs font-semibold text-foreground block truncate max-w-sm">
                    {selectedFileName || 'Document Ready for Extraction'}
                  </span>
                  <span className="text-[11px] text-emerald-600 font-medium">
                    {selectedImageMime === 'application/pdf' ||
                    selectedFileName?.toLowerCase().endsWith('.pdf')
                      ? '✓ PDF document loaded • Ready for Gemini Document Extraction'
                      : '✓ Image loaded • Ready for Gemini Vision'}
                  </span>
                </div>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClearImage}
                className="h-7 text-xs text-muted-foreground hover:text-destructive gap-1"
              >
                <X className="h-3.5 w-3.5" />
                <span>Remove</span>
              </Button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  <Upload className="h-4 w-4" />
                </div>
                <span className="text-xs font-semibold text-foreground">
                  Drop PDF work order, screenshot, or file here, or press{' '}
                  <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-muted rounded border border-border">
                    Ctrl+V
                  </kbd>{' '}
                  to paste
                </span>
              </div>
              <p className="text-[11px] text-muted-foreground max-w-md">
                Supports PDF work orders & compliance certificates, screenshots (PNG/JPG), legacy
                exports (CSV/TSV), and job notes.
              </p>
            </>
          )}

          {/* Textarea for pasting unstructured job text / CSV */}
          <textarea
            value={pasteText}
            onChange={(e) => onPasteTextChange(e.target.value)}
            placeholder="Or paste job details, email requests, or CSV rows here (e.g. 'Fix AC unit for Sarah at 123 George St, 0412345678, HVAC, 60 mins')..."
            className="w-full h-16 p-2 text-xs rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary font-mono resize-none mt-2"
          />

          {/* File picker triggers */}
          <input
            type="file"
            ref={imageInputRef}
            accept="application/pdf,image/png,image/jpeg,image/webp,image/jpg"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleImageFile(file);
            }}
          />
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv,.txt,.tsv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleDocumentFile(file);
            }}
          />
        </div>

        {/* Action Bar */}
        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={() => imageInputRef.current?.click()}
            >
              <FileText className="h-3.5 w-3.5 text-primary" />
              <span>Upload PDF / Screenshot</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs gap-1.5"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileSpreadsheet className="h-3.5 w-3.5 text-muted-foreground" />
              <span>Upload CSV / File</span>
            </Button>
          </div>

          <Button
            type="submit"
            size="sm"
            disabled={(!selectedImageBase64 && !pasteText.trim()) || isExtracting}
            className="gap-2 bg-primary hover:bg-primary/90 h-8"
          >
            {isExtracting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                <span>Extracting with Gemini...</span>
              </>
            ) : (
              <>
                <Sparkles className="h-3.5 w-3.5" />
                <span>Extract Proposals</span>
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
